/** Small Python-semantics helpers used across the port (see PORTING.md). */

/** Python `%`: result has the sign of the divisor. Essential for longitude math. */
export function pymod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/**
 * Python round(): correctly-rounded half-to-even on the EXACT binary value of x,
 * like CPython (which rounds the true decimal expansion of the double, not a
 * float-multiplied approximation). Implemented exactly via the IEEE-754 bits.
 */
export function pythonRound(x: number, ndigits = 0): number {
  if (!Number.isFinite(x)) return x;
  if (x === 0) return 0;
  const neg = x < 0 || Object.is(x, -0);
  const abs = Math.abs(x);

  // Decompose |x| = m * 2^e exactly (m, e integers).
  const buf = new DataView(new ArrayBuffer(8));
  buf.setFloat64(0, abs);
  const hi = BigInt(buf.getUint32(0));
  const lo = BigInt(buf.getUint32(4));
  const bits = (hi << 32n) | lo;
  const biasedExp = Number((bits >> 52n) & 0x7ffn);
  let m = bits & 0xfffffffffffffn;
  let e: number;
  if (biasedExp === 0) {
    e = -1074; // subnormal
  } else {
    m |= 0x10000000000000n;
    e = biasedExp - 1075;
  }

  // Round |x| * 10^ndigits = N / D half-to-even, exactly.
  let N: bigint;
  let D: bigint;
  if (e >= 0) {
    N = m << BigInt(e);
    D = 1n;
  } else {
    N = m;
    D = 1n << BigInt(-e);
  }
  if (ndigits >= 0) N *= 10n ** BigInt(ndigits);
  else D *= 10n ** BigInt(-ndigits);

  let q = N / D;
  const r = N % D;
  const twice = r * 2n;
  if (twice > D || (twice === D && q % 2n === 1n)) q += 1n;

  const result = Number(q) / Math.pow(10, ndigits);
  return neg ? -result : result;
}

/** Python int() for finite numbers: truncate toward zero. */
export function pyint(x: number): number {
  return Math.trunc(x);
}

/**
 * Python truthiness for JSON-shaped values. Shared implementation for the engine modules
 * (scoring / rectification / precision / holdout / rising-sign, whose copies were identical).
 * NOTE: json_store keeps its own deliberately-divergent `_truthy` (plain-object check), and
 * json_store/timezones/config keep local `_py_float`s with different error semantics — those
 * are NOT drop-in replaceable with these.
 */
export function _truthy(value: any): boolean {
  if (value === null || value === undefined || value === false || value === 0 || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
}

/**
 * Python float(): ValueError (-> Error) on a bad string, TypeError on None — the TypeError
 * deliberately escapes the `except (KeyError, ValueError)` guards, matching CPython.
 */
export function _py_float(value: any): number {
  if (value === null || value === undefined) {
    throw new TypeError("float() argument must be a string or a real number, not 'NoneType'");
  }
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const s = value.trim();
    if (s === "" || Number.isNaN(Number(s))) {
      throw new Error(`could not convert string to float: '${value}'`);
    }
    return Number(s);
  }
  throw new TypeError("float() argument must be a string or a real number");
}

/** Python divmod for numbers. */
export function divmod(a: number, b: number): [number, number] {
  const q = Math.floor(a / b);
  return [q, a - q * b];
}

/** sum(xs) with an empty-default of 0. */
export function sum(xs: Iterable<number>): number {
  let t = 0;
  for (const x of xs) t += x;
  return t;
}

export function mean(xs: number[]): number {
  if (!xs.length) throw new Error("mean of empty list");
  return sum(xs) / xs.length;
}

/** statistics.pstdev — population standard deviation. */
export function pstdev(xs: number[]): number {
  const m = mean(xs);
  return Math.sqrt(sum(xs.map((x) => (x - m) ** 2)) / xs.length);
}

/** Python min/max with key, returning the FIRST extremal element (Python tie behavior). */
export function minBy<T>(xs: T[], key: (x: T) => number): T {
  if (!xs.length) throw new Error("minBy of empty list");
  let best = xs[0];
  let bestK = key(best);
  for (let i = 1; i < xs.length; i++) {
    const k = key(xs[i]);
    if (k < bestK) {
      best = xs[i];
      bestK = k;
    }
  }
  return best;
}

export function maxBy<T>(xs: T[], key: (x: T) => number): T {
  if (!xs.length) throw new Error("maxBy of empty list");
  let best = xs[0];
  let bestK = key(best);
  for (let i = 1; i < xs.length; i++) {
    const k = key(xs[i]);
    if (k > bestK) {
      best = xs[i];
      bestK = k;
    }
  }
  return best;
}

/** Stable sort by a Python-style tuple key (lexicographic over numbers/strings). */
export function sortByKey<T>(xs: T[], key: (x: T) => Array<number | string>): T[] {
  return [...xs].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
      const x = ka[i];
      const y = kb[i];
      if (x === undefined) return -1;
      if (y === undefined) return 1;
      if (x < y) return -1;
      if (x > y) return 1;
    }
    return 0;
  });
}

/** Python str.zfill / zero-pad helper. */
export function zfill(n: number | string, width: number): string {
  return String(n).padStart(width, "0");
}

/** range(start, stop, step) as an array (Python semantics). */
export function range(start: number, stop?: number, step = 1): number[] {
  if (stop === undefined) {
    stop = start;
    start = 0;
  }
  const out: number[] = [];
  if (step > 0) for (let i = start; i < stop; i += step) out.push(i);
  else if (step < 0) for (let i = start; i > stop; i += step) out.push(i);
  else throw new Error("range() step must not be zero");
  return out;
}
